import { motion } from "framer-motion";
import { useT } from "@/i18n";
import { fileUrl } from "@/lib/api";
import { Sparkles, Route } from "lucide-react";

const creatureImg = (c) => (c?.image_path ? fileUrl(c.image_path) : c?.image_url || null);

export const MagicStoryCard = ({ magic, creature, luckyCreatures, region, mood, moodEmoji, footer, compact }) => {
  const { t } = useT();
  if (!magic) return null;
  const color = creature?.color || "#D4AF37";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#0A0F1D] p-6 md:p-7"
      style={{ boxShadow: `0 0 30px ${color}22` }}
      data-testid="magic-story-card"
    >
      <div
        className="absolute inset-x-0 top-0 h-1"
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
      />
      <div className="mb-3 flex items-center gap-2 text-xs tracking-[0.2em] text-[#A3B1C6]/70">
        <Sparkles className="h-3.5 w-3.5" style={{ color }} />
        {region || t("story.myth")} {mood && `· ${moodEmoji || ""} ${mood}`}
      </div>
      <h3 className="font-serif text-2xl font-semibold tracking-tight md:text-3xl" style={{ color }}>
        {magic.magic_title}
      </h3>
      <p className="mt-2 font-serif text-lg italic text-[#FDFBF7]/90">{magic.magic_summary}</p>
      {!compact && (
        <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-[#A3B1C6]">
          {magic.magic_story}
        </p>
      )}
      {compact && magic.share_caption && (
        <p className="mt-3 text-sm leading-relaxed text-[#A3B1C6]">{magic.share_caption}</p>
      )}

      {creature?.name && (
        <div className="mt-5 flex items-center gap-3 rounded-xl border border-white/5 bg-black/30 p-3">
          {creatureImg(creature) ? (
            <img
              src={creatureImg(creature)}
              alt={creature.name}
              data-testid="story-creature-image"
              className="h-12 w-12 shrink-0 rounded-full object-cover"
              style={{ border: `1px solid ${color}66`, boxShadow: `0 0 16px ${color}55` }}
            />
          ) : (
            <span
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-lg"
              style={{ background: `${color}22`, boxShadow: `0 0 16px ${color}55` }}
            >
              <Sparkles className="h-5 w-5" style={{ color }} />
            </span>
          )}
          <div>
            <p className="text-sm font-medium text-[#FDFBF7]">{creature.name}</p>
            <p className="text-xs text-[#A3B1C6]">{creature.description}</p>
          </div>
        </div>
      )}

      {!compact && luckyCreatures?.length > 0 && (
        <div className="mt-5" data-testid="lucky-creatures">
          <p className="mb-2 flex items-center gap-1.5 text-xs tracking-[0.2em] text-[#A3B1C6]/70">
            <Sparkles className="h-3.5 w-3.5" style={{ color }} />
            {t("story.lucky_title")} · {luckyCreatures.length}
          </p>
          <div className="flex flex-wrap gap-2">
            {luckyCreatures.map((lc, i) => (
              <motion.div
                key={`${lc.name}-${i}`}
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 + i * 0.12 }}
                className="flex items-center gap-2 rounded-full border px-3 py-1.5"
                style={{ borderColor: `${lc.color || color}55`, background: `${lc.color || color}12` }}
                data-testid="lucky-creature-chip"
              >
                {creatureImg(lc) ? (
                  <img
                    src={creatureImg(lc)}
                    alt={lc.name}
                    className="h-6 w-6 rounded-full object-cover"
                    style={{ border: `1px solid ${lc.color || color}66`, boxShadow: `0 0 10px ${lc.color || color}66` }}
                  />
                ) : (
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full"
                    style={{ background: `${lc.color || color}22`, boxShadow: `0 0 10px ${lc.color || color}66` }}
                  >
                    <Sparkles className="h-3.5 w-3.5" style={{ color: lc.color || color }} />
                  </span>
                )}
                <span className="text-sm text-[#FDFBF7]">{lc.name}</span>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {magic.journey_line && (
        <div className="mt-3 flex items-center gap-2 text-sm" style={{ color }}>
          <Route className="h-4 w-4" />
          {magic.journey_line}
          {creature?.trail_style && (
            <span className="text-[#A3B1C6]">· {creature.trail_style}</span>
          )}
        </div>
      )}

      {footer && <div className="mt-4">{footer}</div>}
    </motion.div>
  );
};
